import '../../../css/Timesheets.css';

import React, { useState } from 'react';

import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import Icon from 'react-icons-kit';
import { ic_check_box as checkboxChecked } from 'react-icons-kit/md/ic_check_box';
import { ic_check_box_outline_blank as checkboxUnchecked } from 'react-icons-kit/md/ic_check_box_outline_blank';
import { ic_sync_problem as syncIssue } from 'react-icons-kit/md/ic_sync_problem';
import { connect } from 'react-redux';

import { getFeatureFlagFromStore } from '../../../hooks/useFeatureFlag.js';
import { figureNameOrDescription, getLockedLabel } from '../../../utils/CommonFunctions.js';
import { idsForWeeklyEntry, isEmptyDuration } from '../../../utils/TimeEntryUtils.js';
import TimeEntryOptions from './TimeEntryOptions.js';

const MAX_DESCRIPTION_LENGTH = 160;
const entityNameToDisplayName = {
    employee: 'Employee',
    customer: 'Customer',
    inventoryItem: 'Service',
    qbClass: 'Class',
    payrollItem: 'Payroll Item',
};

const constructUnknownEntitiesDataTip = unknownEntities => {
    const displayNames = unknownEntities.map(name => entityNameToDisplayName[name]);
    return 'Unknown ' + displayNames.join(', ');
};

const renderWeekdayTotals = (entry, startOfWeek, filterStartDate, filterEndDate) => {
    const startDate = moment(startOfWeek);

    return _.range(0, 7).map(index => {
        const isOutOfRange =
            (filterStartDate && startDate.isBefore(filterStartDate, 'day')) ||
            (filterEndDate && startDate.isAfter(filterEndDate, 'day'));
        const day = startDate.format('ddd');
        const duration = entry.durations[day];
        startDate.add(1, 'd');

        if (isOutOfRange) {
            return (
                <td key={`weekday-${index}`} className="tsDetailsDayCell">
                    <span
                        data-tip="This date is outside of the currently selected date range"
                        className="outOfRange"
                    >
                        &mdash;
                    </span>
                </td>
            );
        }
        return (
            <td key={`weekday-${index}`} className="tsDetailsDayCell">
                {!isEmptyDuration(duration) ? (
                    <span className="nonEmptyTotal">{duration}</span>
                ) : (
                    <span className="emptyTotal">0:00</span>
                )}
            </td>
        );
    });
};

const TimesheetRow = ({
    entry,
    qbType,
    startOfWeek,
    shouldShowPayroll = false,
    onEditEntry = _.noop,
    onDuplicateEntry = _.noop,
    onDeleteEntry = _.noop,
    onSendMessage = _.noop,
    selectedEntryHash,
    selectedEntryIds = [],
    onEntrySelect = _.noop,
    shouldShowBillable = false,
    shouldShowTaxable = false,
    shouldShowQbClasses = false,
    onContinueTimer = _.noop,
    onStartNewTimer = _.noop,
    onOpenTimer = _.noop,
    onToggleBillable = _.noop,
    onToggleTaxable = _.noop,
    onToggleApproved = _.noop,
    isContinuingTimerRow = false,
    isUserClockInOnly,
    updatingBillableEntryIds = [],
    updatingStatusEntryIds = [],
    groupedByWeek,
    filterStartDate,
    filterEndDate,
    noIntegration,
    canLockEntries,
    readOnly,
    featuresStore,
    accountStore,
}) => {
    const { isEnabled: showNoteField } = getFeatureFlagFromStore('show_note_field', featuresStore);
    const { isEnabled: hideDescriptionField } = getFeatureFlagFromStore(
        'hide_description_field',
        featuresStore
    );
    const { isEnabled: showClockInClockOut } = getFeatureFlagFromStore(
        'show_clock_in_clock_out',
        featuresStore
    );
    const employee = entry.Employee;
    const customer = entry.Customer;
    const inventoryItem = entry.InventoryItem;
    const qbClass = entry.QbClass;
    const payrollItem = entry.PayrollItem;
    const unknownEntities = entry.unknown_entities;
    const isAbleToSync = entry.unknown_entities.length === 0 && entry.sync_error === null;

    const employeeName = figureNameOrDescription(employee),
        customerName = figureNameOrDescription(customer),
        inventoryItemName = figureNameOrDescription(inventoryItem),
        qbClassName = figureNameOrDescription(qbClass),
        payrollItemName = figureNameOrDescription(payrollItem);

    const uuid = 'uuid' in entry ? entry.uuid : entry.id;
    const isBillable = entry.billable === 'Yes';
    const isTaxable = entry.taxable === 'Yes';
    const isSynced = entry.exported === 'Yes';
    const isLocked = entry.locked === 'Yes';
    const isApprovedForSyncing = entry.approved === 'Yes';
    let isUpdatingBillable = false;
    let isUpdatingStatus = false;
    if (groupedByWeek) {
        const ids = idsForWeeklyEntry(entry);
        isUpdatingBillable = updatingBillableEntryIds.some(id => ids.includes(id));
        isUpdatingStatus = updatingStatusEntryIds.some(id => ids.includes(id));
    } else {
        isUpdatingBillable = updatingBillableEntryIds.includes(entry.id);
        isUpdatingStatus = updatingStatusEntryIds.includes(entry.id);
    }
    const canApprove = entry.can_be_approved_by_current_user;
    const canEdit = entry.can_be_edited_by_current_user;
    const isSelected = selectedEntryIds.includes(uuid);
    const date = moment(entry.date).format('MMM D, YYYY');

    const shouldTruncate = entry.description
        ? entry.description.length > MAX_DESCRIPTION_LENGTH
        : false;
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const description =
        shouldTruncate && !isDescriptionExpanded
            ? _.truncate(entry.description, { length: MAX_DESCRIPTION_LENGTH, separator: ' ' })
            : entry.description;
    const approvedText = noIntegration ? 'Approved' : 'Approved for Sync';

    return (
        <tr
            className={classNames({
                detailsRow: true,
                editing: _.isEqual(entry.week_hash, selectedEntryHash),
                selected: isSelected,
            })}
        >
            <td className="tsSelectionCell">
                {(canEdit || canApprove) && !readOnly && isAbleToSync && (
                    <Icon
                        className="icon"
                        icon={isSelected ? checkboxChecked : checkboxUnchecked}
                        size={18}
                        onClick={onEntrySelect.bind(this, uuid)}
                    />
                )}

                {!isAbleToSync && !isSynced && (
                    <span
                        sync-data-tip={
                            unknownEntities.length > 0
                                ? constructUnknownEntitiesDataTip(unknownEntities)
                                : entry.sync_error
                        }
                    >
                        <Icon className="syncIssueIcon" icon={syncIssue} size={30} />
                    </span>
                )}
            </td>
            <td className="tsEmployeeCell">{employeeName}</td>

            <td className="tsCustomerJobDescriptionCell">
                <strong className="tsCustomerName">{customerName}</strong>

                <span className="tsInventoryItemName">{inventoryItemName}</span>

                {
                    <span
                        className={classNames({
                            tsBillableTag: true,
                            tsEditableBillableTag: canEdit && shouldShowBillable,
                            isBillable,
                        })}
                        onClick={
                            canEdit && shouldShowBillable ? () => onToggleBillable(entry) : null
                        }
                    >
                        {isUpdatingBillable
                            ? 'Updating...'
                            : isBillable
                            ? 'Billable'
                            : 'Not Billable'}
                    </span>
                }

                {qbType === 'Online' && (
                    <span
                        className={classNames({
                            tsTaxableTag: true,
                            tsEditableTaxableTag: canEdit && shouldShowTaxable,
                            isTaxable,
                        })}
                        onClick={
                            canEdit && shouldShowTaxable && isBillable
                                ? () => onToggleTaxable(entry)
                                : null
                        }
                    >
                        {isUpdatingBillable ? 'Updating...' : isTaxable ? 'Taxable' : 'Not Taxable'}
                    </span>
                )}

                {shouldShowQbClasses && qbClassName && (
                    <div className="tsQbClassName">{qbClassName}</div>
                )}
                {shouldShowPayroll && payrollItemName && (
                    <div className="tsPayrollItemName">{payrollItemName}</div>
                )}

                {!hideDescriptionField && description && (
                    <p className="entryDescriptionContainer">
                        {description}{' '}
                        {shouldTruncate && (
                            <button
                                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                className="tertiaryButton"
                            >
                                {isDescriptionExpanded ? 'Less' : 'More'}
                            </button>
                        )}
                    </p>
                )}
            </td>

            {groupedByWeek &&
                renderWeekdayTotals(entry, startOfWeek, filterStartDate, filterEndDate)}

            {!groupedByWeek && <td className="tsDateCell">{date}</td>}

            {!groupedByWeek && (
                <td className="tsDurationCell">
                    <div>{entry.duration}</div>
                    {showClockInClockOut &&
                        entry.start_time &&
                        entry.end_time &&
                        entry.start_time !== entry.end_time && (
                            <div className="tsStartEndTime">
                                {moment(entry.start_time, 'HH:mm').format('h:mm A')} -{' '}
                                {moment(entry.end_time, 'HH:mm').format('h:mm A')}
                            </div>
                        )}
                </td>
            )}

            {showNoteField && <td className="tsNoteCell">{entry.note || ''}</td>}

            <td className="tsStatusCell">
                {isSynced && (
                    <span className="tsStatus tsStatusSynced">
                        {isUpdatingStatus ? 'Updating...' : 'Synced'}
                    </span>
                )}
                {isLocked && noIntegration && (
                    <span className="tsStatus tsStatusApproved">
                        {isUpdatingStatus ? 'Updating...' : getLockedLabel(accountStore)}
                    </span>
                )}
                {isApprovedForSyncing && !isSynced && canApprove && !isLocked && (
                    <span
                        className="tsStatus tsStatusApproved"
                        onClick={() => canLockEntries && onToggleApproved(entry, noIntegration)}
                        data-testid={`timesheets-row-${uuid}-reject-btn`}
                    >
                        {isUpdatingStatus ? 'Updating...' : approvedText}
                    </span>
                )}
                {!isApprovedForSyncing && !isSynced && canApprove && !isLocked && (
                    <span
                        className="tsStatus tsStatusPending"
                        onClick={() => onToggleApproved(entry, noIntegration)}
                        data-testid={`timesheets-row-${uuid}-approve-btn`}
                    >
                        {isUpdatingStatus ? 'Updating...' : 'Pending Approval'}
                    </span>
                )}
            </td>
            {!readOnly && (
                <td className="actionsCell">
                    <TimeEntryOptions
                        entry={entry}
                        entryHash={entry.week_hash}
                        groupedIds={entry.grouped_time_ids}
                        onEditEntry={onEditEntry}
                        onDuplicateEntry={onDuplicateEntry}
                        onSendMessagePressed={onSendMessage}
                        onDeletePressed={onDeleteEntry}
                        onToggleApproved={onToggleApproved}
                        onToggleBillable={onToggleBillable}
                        onToggleTaxable={onToggleTaxable}
                        onContinueTimer={onContinueTimer}
                        onStartNewTimer={onStartNewTimer}
                        onOpenTimer={onOpenTimer}
                        canApprove={canApprove}
                        canEdit={canEdit}
                        groupedByWeek={groupedByWeek}
                        isContinuingTimerRow={isContinuingTimerRow}
                        shouldShowBillable={shouldShowBillable}
                        shouldShowTaxable={shouldShowTaxable}
                        isUserClockInOnly={isUserClockInOnly}
                        noIntegration={noIntegration}
                        canLockEntries={canLockEntries}
                        accountStore={accountStore}
                    />
                </td>
            )}
        </tr>
    );
};

const mapStateToProps = state => ({
    featuresStore: state.featuresStore,
    accountStore: state.accountStore,
});

export default connect(mapStateToProps)(TimesheetRow);
